import { ChatItem, ProgressStep, SystemMessageItem } from '@/lib/type'
import { incrementalHash, messagesHash } from '@/lib/utils'
import { createSlice, PayloadAction } from '@reduxjs/toolkit'

interface ChatState {
  messages: ChatItem[]
  isChatSending: boolean
  isChatAttaching: boolean
  isInitializing: boolean
  pendingInitialMessage: string | null
  lastProgressMessageId: string | null
  preservedMessages: ChatItem[]
  clarificationInProgress: boolean
  messageHash?: string
}

export const initialState: ChatState = {
  messages: [],
  isChatSending: false,
  isChatAttaching: false,
  isInitializing: true,
  pendingInitialMessage: null,
  lastProgressMessageId: null,
  preservedMessages: [],
  clarificationInProgress: false,
}

const chatSlice = createSlice({
  name: 'chat',
  initialState,
  reducers: {
    appendMessage: (state: ChatState, action: PayloadAction<ChatItem>) => {
      const { messageId } = action.payload
      console.log('appendMessage called with:', action.payload)
      const activeItem = state.messages.find((m) => m.messageId === action.payload.messageId)

      if (activeItem) {
        console.log('Updating existing message:', messageId)
        state.messages = state.messages.map((m) =>
          m.messageId === messageId
            ? {
                ...activeItem,
                ...action.payload,
                ...('message' in activeItem &&
                'message' in action.payload &&
                activeItem.message &&
                action.payload.message
                  ? { message: activeItem.message + action.payload.message }
                  : {}),
              }
            : m
        )
      } else {
        console.log('Adding new message:', messageId)

        // sometimes the system message fires at the same time as an assistant message,
        // not giving enough time for the typewriter effect to be applied to the assistant message
        // so only reset previous assistant messages typewriter effect if there is a
        // new typewriter effect.
        if (action.payload.sender === 'system') {
          state.messages.push(action.payload)
        } else {
          state.messages = state.messages.map((m) => {
            if (m.sender === 'assistant') {
              return {
                ...m,
                // reset typewriter effect for all previous messages
                typewriterEffect: false,
              }
            }
            return m
          })
          state.messages.push(action.payload)
        }
      }

      // Update the message hash
      state.messageHash = incrementalHash(state.messageHash, action.payload)

      console.log('Current messages:', state.messages)
    },
    clearMessages: (state: ChatState) => {
      state.messages = []
      state.messageHash = undefined
    },
    setChatSending: (state: ChatState, action: PayloadAction<boolean>) => {
      state.isChatSending = action.payload
    },
    setChatAttaching: (state: ChatState, action: PayloadAction<boolean>) => {
      state.isChatAttaching = action.payload
    },
    addLoadingMessage: (state: ChatState, action: PayloadAction<string>) => {
      const loadingMessage: SystemMessageItem = {
        messageId: action.payload,
        sender: 'system',
        type: 'loading',
      }
      state.messages.push(loadingMessage)
    },
    removeAllLoadingMessages: (state: ChatState) => {
      state.messages = state.messages.filter(
        (m) => !(m.sender === 'system' && 'type' in m && m.type === 'loading')
      )
    },
    replaceMessages: (
      state: ChatState,
      action: PayloadAction<{ messages: ChatItem[]; messageHash: string }>
    ) => {
      console.log(`replaceMessages called with ${action.payload.messages.length} new messages`)

      // Clear existing messages
      state.messages = []

      // Add all new messages at once
      state.messages.push(...action.payload.messages)
      state.messageHash = action.payload.messageHash
    },
    setPendingInitialMessage: (state, action: PayloadAction<string>) => {
      state.pendingInitialMessage = action.payload
    },
    clearPendingInitialMessage: (state) => {
      state.pendingInitialMessage = null
    },
    setLastProgressMessageExpanded: (state: ChatState, action: PayloadAction<boolean>) => {
      const messageIndex = state.messages.findIndex(
        (m) => m.messageId === state.lastProgressMessageId
      )
      if (messageIndex !== -1) {
        const message = state.messages[messageIndex]
        if (message.sender === 'system' && 'type' in message && message.type === 'progress') {
          const progressMessage = message as unknown as SystemMessageItem & {
            type: 'progress'
            expanded: boolean
          }
          state.messages[messageIndex] = {
            ...progressMessage,
            expanded: action.payload,
          } as unknown as ChatItem
        }
      }
    },
    setLastProgressMessageId: (state, action: PayloadAction<string | null>) => {
      state.lastProgressMessageId = action.payload
    },
    appendSubProgressMessage: (
      state: ChatState,
      action: PayloadAction<{
        messageId: string
        step: ProgressStep
      }>
    ) => {
      const { messageId, step } = action.payload
      const messageIndex = state.messages.findIndex((m) => m.messageId === messageId)

      if (messageIndex !== -1) {
        const message = state.messages[messageIndex]
        if (message.sender === 'system' && 'type' in message && message.type === 'progress') {
          const progressMessage = message as unknown as SystemMessageItem & {
            type: 'progress'
            steps: ProgressStep[]
          }
          state.messages[messageIndex] = {
            ...progressMessage,
            steps: [...(progressMessage.steps ?? []), step],
          }
        }
      }
    },
    updateSubProgressMessage: (
      state: ChatState,
      action: PayloadAction<{
        messageId: string
        stepId: string
        status: 'pending' | 'in-progress' | 'success' | 'error'
        errorMessage?: string
      }>
    ) => {
      const { messageId, stepId, status, errorMessage } = action.payload
      const messageIndex = state.messages.findIndex((m) => m.messageId === messageId)

      if (messageIndex !== -1) {
        const message = state.messages[messageIndex]
        if (message.sender === 'system' && 'type' in message && message.type === 'progress') {
          const progressMessage = message as unknown as SystemMessageItem & {
            type: 'progress'
            steps: ProgressStep[]
          }
          const updatedSteps = (progressMessage.steps ?? []).map((step: ProgressStep) =>
            step.id === stepId
              ? { ...step, status, ...(errorMessage ? { errorMessage } : {}) }
              : step
          )
          state.messages[messageIndex] = {
            ...progressMessage,
            steps: updatedSteps,
          } as unknown as ChatItem
        }
      }
    },
    preserveMessages: (state: ChatState) => {
      state.preservedMessages = [...state.messages]
    },
    restoreMessages: (state: ChatState) => {
      state.messages = [...state.preservedMessages]
      state.messageHash = messagesHash(state.messages)
    },
    clearPreservedMessages: (state: ChatState) => {
      state.messages = []
    },
    setClarificationInProgress: (state, action: PayloadAction<boolean>) => {
      state.clarificationInProgress = action.payload
    },
    setMessageHash: (state, action: PayloadAction<string>) => {
      state.messageHash = action.payload
    },
  },
})

export const {
  appendMessage,
  replaceMessages,
  clearMessages,
  setChatSending,
  setChatAttaching,
  addLoadingMessage,
  removeAllLoadingMessages,
  setPendingInitialMessage,
  clearPendingInitialMessage,
  setLastProgressMessageExpanded,
  setLastProgressMessageId,
  appendSubProgressMessage,
  updateSubProgressMessage,
  preserveMessages,
  restoreMessages,
  clearPreservedMessages,
  setClarificationInProgress,
  setMessageHash,
} = chatSlice.actions

export default chatSlice.reducer
